import React from 'react';
import embed from './EmbeddableChat.module.css';
import chat from './ChatInterface.module.css';
import ChatInputBar from './ChatInputBar';
import { BetaDisclaimer, LegalDisclaimer } from './ChatDisclaimers';

/**
 * TRAC-959: the loading skeleton is the chat's own chrome with the content
 * blanked, not a drawing of it.
 *
 * The first cut of the skeleton approximated the chat: a header without its
 * buttons, a single-line composer pill, no banner slot. Every one of those
 * differences became a jump at first paint — the header grew, the composer
 * changed shape, the banner shoved the greeting down. So this file renders the
 * real header/banner/composer elements (same components, same classes) and
 * only the message canvas is placeholders. Nothing can move at first paint
 * because nothing is different.
 *
 * It is also colour-agnostic. The chat opens blue (agent) on tire pages and red
 * (connect-to-a-person) everywhere else, and the skeleton cannot know which
 * until the page context and the live-chat hours are in. Guessing blue meant
 * every non-tire page opened blue, went grey, then went red. So every moment
 * before the opening is known wears UNDECIDED_HEADER, and the brand colour
 * appears exactly once, when it is the answer.
 *
 * loader.js paints an inline copy of this before the app has even downloaded
 * (mirror of EmbedSkeleton — change one, change both;
 * frontend/scripts/skeleton-drift/drift.mjs measures shell, skeleton and live
 * chat against each other).
 */

/** Neither lane's colour: a slate that belongs to neither the agent nor live
 *  chat, so settling into either one afterwards reads as an answer rather than
 *  a correction. (Was EmbedPage's DECIDING_GREY; the same slate now covers the
 *  whole pre-decision window, not just the last beat of it.) */
export const UNDECIDED_HEADER = '#5c6672';

/** The header's identity slot — a mark and a name — as two pulsing blanks the
 *  size of "AI Assist BETA". The real logo/headset and title land in place. */
export const SkeletonTitle: React.FC = () => (
  <span className={embed.skeletonTitle} aria-hidden>
    <span className={embed.skeletonTitleMark} />
    <span className={embed.skeletonTitleText} />
  </span>
);

/** Three lines where the greeting paragraph lands, three pills where the
 *  starter prompts land (the tire-page opening's shape; see the CSS). */
export const SkeletonCanvas: React.FC<{ inset?: boolean }> = ({ inset = false }) => (
  <div className={`${embed.skeletonCanvas}${inset ? ` ${embed.skeletonCanvasInset}` : ''}`} aria-hidden>
    <div className={embed.skeletonLine} style={{ width: '90%' }} />
    <div className={embed.skeletonLine} style={{ width: '84%' }} />
    <div className={embed.skeletonLine} style={{ width: '38%' }} />
    <div className={embed.skeletonPills}>
      <div className={embed.skeletonPill} style={{ width: 236 }} />
      <div className={embed.skeletonPill} style={{ width: 152 }} />
      <div className={embed.skeletonPill} style={{ width: 170 }} />
    </div>
  </div>
);

/** The connect-banner slot, blank. Same wrapper and button box as the live
 *  banner (ChatInterface), so the banner resolves in place instead of appearing. */
export const SkeletonBanner: React.FC = () => (
  <div className={chat.modeBannerWrap} aria-hidden>
    <div className={`${chat.modeBanner} ${chat.modeBannerGhost}`}>&nbsp;</div>
  </div>
);

interface EmbedSkeletonProps {
  /** EmbedPage's widget root class (chatWidget + any layout modifier). */
  className: string;
  /** How many buttons the live header will show, so the header is already that wide. */
  headerButtons: number;
}

const noop = (): void => {};

/**
 * The whole widget, blanked: what EmbedPage shows from mount until the chat
 * can draw its opening.
 */
export const EmbedSkeleton: React.FC<EmbedSkeletonProps> = ({ className, headerButtons }) => {
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  return (
    <div className={className} data-skeleton="embed">
      <div className={embed.widgetHeader} style={{ background: UNDECIDED_HEADER }}>
        <div className={embed.headerLeft}>
          <SkeletonTitle />
          {/* The blanks are decorative; this is what the frame means, for
              screen readers — the same words the old loading overlay said. */}
          <span className={embed.srOnly} role="status">Loading…</span>
        </div>
        <div className={embed.headerButtons} aria-hidden>
          {Array.from({ length: headerButtons }, (_, i) => (
            <span key={i} className={embed.headerButton} />
          ))}
        </div>
      </div>
      {/* Same container the chat renders into, so the flex maths below the
          header is identical. `inert` keeps the real composer inside from
          taking focus or keystrokes before the chat exists. */}
      <div
        className={`${chat.chatContainer} ${chat.embedChatContainer}`}
        style={{ pointerEvents: 'none' }}
        aria-hidden
        {...({ inert: '' } as Record<string, string>)}
      >
        <SkeletonBanner />
        <div className={`${chat.messagesWrapper} ${chat.embedMessagesWrapper} ${chat.embedMessagesWrapperNoScroll}`}>
          <SkeletonCanvas />
        </div>
        <BetaDisclaimer />
        <ChatInputBar
          inputRef={inputRef}
          inputValue=""
          onChangeText={noop}
          onSend={noop}
          ymmvMode={false}
          ymmvStep="vehicle"
          emergencyActive={false}
          isDemoMode={false}
          highlightInput={false}
          isLoading={false}
          isStreaming={false}
          pendingSelection={false}
          retryAttempt={null}
          handoffActive={false}
          handoffAgentName=""
          preConversation
        />
        <LegalDisclaimer />
      </div>
    </div>
  );
};
